import React from 'react'
import { noticeError } from '@lib/newrelic/agent'

type ErrorBoundaryProps = {
    children?: React.ReactNode
    /** UI opcional a mostrar cuando un error de render es capturado. */
    fallback?: React.ReactNode
}

type ErrorBoundaryState = {
    hasError: boolean
}

/**
 * ErrorBoundary de React que reporta errores de render a New Relic vía
 * `noticeError`. Cubre el hueco que el feature `jserrors` del agente no ve
 * (los errores de render de componentes no llegan a window.onerror).
 *
 * Sin `fallback` no renderiza nada en estado de error (igual que un crash de
 * React sin boundary, pero ahora con telemetría). Una pantalla de error más
 * rica puede añadirse luego vía la prop `fallback`.
 */
export class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true }
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        noticeError(error, {
            source: 'react-error-boundary',
            componentStack: info.componentStack ?? ''
        })
    }

    render(): React.ReactNode {
        if (this.state.hasError) {
            return this.props.fallback ?? null
        }

        return this.props.children
    }
}

export default ErrorBoundary
